import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DateRange } from "@/hooks/useDateRange";
import type { TeamSegment } from "@/components/pages/analytics/front-desk/components/team/types";
import { mapOverviewToSummary } from "@/components/pages/analytics/front-desk/components/team/teamsTabMappers";
import {
  getPreviousAnalyticsDateRange,
  toAnalyticsDateParam,
} from "@/components/pages/analytics/front-desk/utils/analyticsDateUtils";
import { analyticsTeamsService } from "@/services/analytics-teams-service";
import { analyticsTenantService } from "@/services/analytics-tenant-service";
import { partnerDashboardService } from "@/services/partner-dashboard-service";
import type { TeamsOverviewResponse } from "@/types/analytics-teams";
import type { AdminHotelMetricRow } from "../types/adminHotelMetricTable";
import {
  buildAdminTeamKpiComparison,
  mapTeamHotelRows,
  type TeamKpiComparisonByKey,
} from "../utils/adminTeamMappers";
import { useAdminTenantId } from "./useAdminTenantId";

function isDateRangeReady(dateRange: DateRange): boolean {
  return Boolean(
    toAnalyticsDateParam(dateRange.from) && toAnalyticsDateParam(dateRange.to),
  );
}

async function fetchHotelOverview(
  hotelId: string,
  from: string,
  to: string,
): Promise<TeamsOverviewResponse | null> {
  try {
    return await analyticsTeamsService.getOverview({ hotelId, from, to });
  } catch {
    return null;
  }
}

async function fetchAdminTeamData(
  tenantId: string,
  from: string,
  to: string,
  previousFrom: string,
  previousTo: string,
  segment: TeamSegment,
) {
  const rangeQuery = { from, to };
  const prevRangeQuery = { from: previousFrom, to: previousTo };

  const teamsTable = await analyticsTenantService.getTeamsTable(rangeQuery);
  const hotels = teamsTable.rows;

  const [
    overview,
    previousOverview,
    currentHotelOverviews,
    previousHotelOverviews,
    hotelOptions,
  ] = await Promise.all([
    analyticsTenantService.getTeamsOverview(rangeQuery),
    analyticsTenantService.getTeamsOverview(prevRangeQuery),
    Promise.all(
      hotels.map((hotel) => fetchHotelOverview(hotel.hotelId, from, to)),
    ),
    Promise.all(
      hotels.map((hotel) =>
        fetchHotelOverview(hotel.hotelId, previousFrom, previousTo),
      ),
    ),
    partnerDashboardService
      .getHotelFilterOptions(tenantId)
      .catch(
        () =>
          [] as Awaited<
            ReturnType<typeof partnerDashboardService.getHotelFilterOptions>
          >,
      ),
  ]);

  const hotelLogosById = Object.fromEntries(
    hotelOptions.map((hotel) => [hotel.id, hotel.logoId]),
  );

  const currentByHotelId = new Map(
    hotels.map((hotel, index) => [hotel.hotelId, currentHotelOverviews[index]]),
  );
  const previousByHotelId = new Map(
    hotels.map((hotel, index) => [
      hotel.hotelId,
      previousHotelOverviews[index],
    ]),
  );

  return {
    summary: mapOverviewToSummary(overview),
    kpiComparison: buildAdminTeamKpiComparison(
      overview,
      previousOverview,
      segment,
    ),
    tableRows: mapTeamHotelRows(
      hotels,
      currentByHotelId,
      previousByHotelId,
      hotelLogosById,
      segment,
    ),
  };
}

export const adminTeamKeys = {
  all: ["admin-analytics-team"] as const,
  hotels: (tenantId: string, from: string, to: string, segment: TeamSegment) =>
    [...adminTeamKeys.all, tenantId, from, to, segment] as const,
};

export function useAdminTeamHotels(dateRange: DateRange, segment: TeamSegment) {
  const tenantId = useAdminTenantId();
  const from = toAnalyticsDateParam(dateRange.from) ?? "";
  const to = toAnalyticsDateParam(dateRange.to) ?? "";
  const previousRange = getPreviousAnalyticsDateRange(dateRange);
  const previousFrom = toAnalyticsDateParam(previousRange?.from) ?? "";
  const previousTo = toAnalyticsDateParam(previousRange?.to) ?? "";
  const isReady = Boolean(
    tenantId && isDateRangeReady(dateRange) && previousFrom && previousTo,
  );

  const query = useQuery({
    queryKey: adminTeamKeys.hotels(tenantId ?? "", from, to, segment),
    queryFn: () =>
      fetchAdminTeamData(
        tenantId!,
        from,
        to,
        previousFrom,
        previousTo,
        segment,
      ),
    enabled: isReady,
  });

  const summary = useMemo(
    () =>
      query.data?.summary ?? {
        tasksCompleted: 0,
        tasksCancelled: 0,
        avgTimeOnTask: "0s",
        delays: 0,
        daysOff: 0,
      },
    [query.data?.summary],
  );

  const kpiComparison = useMemo<TeamKpiComparisonByKey>(
    () => query.data?.kpiComparison ?? {},
    [query.data?.kpiComparison],
  );

  const tableRows = useMemo<AdminHotelMetricRow[]>(
    () => query.data?.tableRows ?? [],
    [query.data?.tableRows],
  );

  return {
    tenantId,
    summary,
    kpiComparison,
    tableRows,
    isLoading: query.isLoading,
    isError: query.isError,
    isReady,
  };
}
